import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Receipt, Pencil, Trash2, Download, TrendingDown, Calendar, Tag, RefreshCw } from 'lucide-react';
import { getErrorMessage } from '@/lib/errorUtils';
import { format } from 'date-fns';

import { API } from '@/config/api';
const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const PREDEFINED_CATEGORIES = [
  { value: 'utilities', label: 'Utilities' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'salaries', label: 'Salaries' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'taxes', label: 'Taxes' },
  { value: 'other', label: 'Other' },
];

const ExpensesPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { hotel } = useHotel();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [summary, setSummary] = useState({ total: 0, by_category: {} });
  const [dateFilter, setDateFilter] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  const [form, setForm] = useState({
    category: 'other',
    amount: '',
    description: '',
    vendor: '',
    receipt_number: '',
    expense_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [newCategory, setNewCategory] = useState({ name: '', description: '' });

  const fetchExpenses = useCallback(async (showRefreshing = false) => {
    if (!user?.hotel_id) return;
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [expRes, catRes, sumRes] = await Promise.all([
        axios.get(`${API}/hotels/${user.hotel_id}/expenses?from_date=${dateFilter.from}&to_date=${dateFilter.to}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/expense-categories`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/expenses/summary/by-category?from_date=${dateFilter.from}&to_date=${dateFilter.to}`, { headers: getAuthHeaders() })
      ]);
      setExpenses(expRes.data);
      // Flatten categories: combine predefined and custom into single array
      const catData = catRes.data;
      const flatCategories = [
        ...(catData.predefined || []).map(c => ({ ...c, predefined: true })),
        ...(catData.custom || []).map(c => ({ ...c, predefined: false }))
      ];
      setCategories(flatCategories);
      
      // Parse summary data - it's an array of {category, total, count}
      const summaryArray = sumRes.data || [];
      const totalExpenses = summaryArray.reduce((sum, cat) => sum + (cat.total || 0), 0);
      const totalCount = summaryArray.reduce((sum, cat) => sum + (cat.count || 0), 0);
      const byCategory = {};
      summaryArray.forEach(cat => {
        byCategory[cat.category] = cat.total || 0;
      });
      setSummary({ total: totalExpenses, count: totalCount, by_category: byCategory });
      setLastUpdated(new Date());
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.hotel_id, dateFilter, getAuthHeaders]);

  // Initial fetch and when date filter changes
  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Auto-refresh
  useEffect(() => {
    const intervalId = setInterval(() => fetchExpenses(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchExpenses]);

  const handleManualRefresh = () => fetchExpenses(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        category: form.category,
        amount: parseFloat(form.amount),
        description: form.description,
        vendor: form.vendor || null,
        receipt_number: form.receipt_number || null,
        date: form.expense_date,  // Backend expects 'date', not 'expense_date'
        notes: form.notes || null
      };
      
      if (editingExpense) {
        await axios.put(`${API}/hotels/${user.hotel_id}/expenses/${editingExpense.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/hotels/${user.hotel_id}/expenses`, payload, { headers: getAuthHeaders() });
        toast.success('Expense added');
      }
      setDialogOpen(false);
      resetForm();
      // Refresh data immediately after adding/updating
      await fetchExpenses(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save expense'));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/hotels/${user.hotel_id}/expenses/${id}`, { headers: getAuthHeaders() });
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hotels/${user.hotel_id}/expense-categories?name=${encodeURIComponent(newCategory.name)}&description=${encodeURIComponent(newCategory.description || '')}`, {}, { headers: getAuthHeaders() });
      toast.success('Category added');
      setCategoryDialogOpen(false);
      setNewCategory({ name: '', description: '' });
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/hotels/${user.hotel_id}/expense-categories/${id}`, { headers: getAuthHeaders() });
      toast.success('Category deleted');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const exportPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/hotels/${user.hotel_id}/reports/expenses/pdf?from_date=${dateFilter.from}&to_date=${dateFilter.to}`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_${dateFilter.from}_${dateFilter.to}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const resetForm = () => {
    setForm({ category: 'other', amount: '', description: '', vendor: '', receipt_number: '', expense_date: new Date().toISOString().split('T')[0], notes: '' });
    setEditingExpense(null);
  };

  const openEdit = (expense) => {
    setEditingExpense(expense);
    setForm({
      category: expense.category,
      amount: expense.amount.toString(),
      description: expense.description,
      vendor: expense.vendor || '',
      receipt_number: expense.receipt_number || '',
      expense_date: expense.date ? expense.date.split('T')[0] : new Date().toISOString().split('T')[0],
      notes: expense.notes || ''
    });
    setDialogOpen(true);
  };

  const getCategoryLabel = (value) => {
    const found = categories.find(c => c.id === value || c.name?.toLowerCase() === value?.toLowerCase());
    if (found) return found.name;
    return value?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const currency = hotel?.currency || 'EUR';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Expenses</h1>
          <p className="text-muted-foreground">
            Track and manage hotel expenses
            {lastUpdated && <span className="ml-2 text-xs">• Updated: {format(lastUpdated, 'HH:mm:ss')}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outline" onClick={exportPDF} data-testid="export-expenses-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-category-btn">
                <Tag className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Custom Category</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddCategory} className="space-y-4">
                <div className="space-y-2">
                  <Label>Category Name</Label>
                  <Input
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    required
                    data-testid="category-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Input
                    value={newCategory.description}
                    onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    data-testid="category-description-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="save-category-btn">Add Category</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-expense-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="expense-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PREDEFINED_CATEGORIES.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                        {categories.filter(c => !c.predefined).map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Amount ({currency})</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      required
                      data-testid="expense-amount-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    required
                    data-testid="expense-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vendor</Label>
                    <Input
                      value={form.vendor}
                      onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                      data-testid="expense-vendor-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Receipt #</Label>
                    <Input
                      value={form.receipt_number}
                      onChange={(e) => setForm({ ...form, receipt_number: e.target.value })}
                      data-testid="expense-receipt-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={form.expense_date}
                    onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                    required
                    data-testid="expense-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={2}
                    data-testid="expense-notes-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="save-expense-btn">
                  {editingExpense ? 'Update Expense' : 'Add Expense'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-2xl font-bold">{currency} {summary.total?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Receipt className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Records</p>
                <p className="text-2xl font-bold">{summary.count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="md:col-span-2">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">Top Categories</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.by_category || {}).slice(0, 4).map(([cat, amt]) => (
                <Badge key={cat} variant="secondary" className="text-xs">
                  {getCategoryLabel(cat)}: {currency} {amt.toFixed(2)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                From Date
              </Label>
              <Input
                type="date"
                value={dateFilter.from}
                onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })}
                data-testid="filter-from-date"
              />
            </div>
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={dateFilter.to}
                onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })}
                data-testid="filter-to-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Expense Records
          </CardTitle>
        </CardHeader>
        <CardContent>
          {expenses.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No expenses found for this period</p>
              <p className="text-sm">Click "Add Expense" to record your first expense</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                    <TableCell>{expense.date ? format(new Date(expense.date), 'MMM dd, yyyy') : 'No date'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{getCategoryLabel(expense.category)}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{expense.description}</TableCell>
                    <TableCell>{expense.vendor || '-'}</TableCell>
                    <TableCell className="text-right font-medium">
                      {currency} {expense.amount.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(expense)} data-testid={`edit-expense-${expense.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(expense.id)} data-testid={`delete-expense-${expense.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Custom Categories */}
      {categories.filter(c => !c.predefined).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Custom Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {categories.filter(c => !c.predefined).map((cat) => (
                <Badge key={cat.id} variant="secondary" className="text-sm py-1 px-3 flex items-center gap-2">
                  {cat.name}
                  <button onClick={() => handleDeleteCategory(cat.id)} className="hover:text-red-500">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExpensesPage;
